import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import {
    GetRelatedProductCollectionApiV2Parameter,
    getRelatedProductsCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";
import translate from "@insite/client-framework/Translate";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import Icon from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import ProductDetailsKitSection from "@MasterBluePrint/Components/ProductDetailsKitSection";
import PdfIcon from "@MasterBluePrint/Icons/PdfIcon";
import transformContentHtml from "@MasterBluePrint/Overrides/Common/Utilities/transformContentHtml";
import parse from "html-react-parser";
import isMobile from "ismobilejs";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
    showKitsProducts = "showKitsProducts",
    showAccessoryProducts = "showAccessoryProducts",
}

type Props = WidgetProps & HasProduct;

class RelatedProductsService {
    static instance: null | RelatedProductsService = null;
    dataCache: { [key: string]: { kitsCollection: ProductModel[] | null; accessoryCollection: ProductModel[] | null; isDataFetched: boolean; } } = {};
    onGoingRequests: any = {};

    constructor() {
        if (!RelatedProductsService.instance) {
            RelatedProductsService.instance = this;
            this.dataCache = {};
            this.onGoingRequests = {};
        }
        return RelatedProductsService.instance;
    }

    fetchRelatedProducts(productId: string) {

        if (this.dataCache[productId]) {
            return this.dataCache[productId];
        }

        if (this.onGoingRequests[productId]) {
            return this.onGoingRequests[productId];
        }

        const request = this.loadRelatedProducts(productId);
        this.onGoingRequests[productId] = request;
        return request;
    }

    private async loadRelatedProducts(productId: string) {
        try {
            const [kits, accessories] = await Promise.all([
                getRelatedProductsCollectionV2({ productId, relationship: "KitContents", pageSize: 999 }),
                getRelatedProductsCollectionV2({ productId, relationship: "Accessories", pageSize: 999 })
            ]);

            const fetchedData = {
                kitsCollection: kits.products,
                accessoryCollection: accessories.products,
                isDataFetched: true,
            };

            this.dataCache[productId] = fetchedData;
            return fetchedData;
        } catch (error) {
            logger.error('Error fetching related products:', error);
            throw error;
        } finally {
            delete this.onGoingRequests[productId];
        }
    }

    static getInstance() {
        if (RelatedProductsService.instance === null) {
            RelatedProductsService.instance = new RelatedProductsService();
        }
        return RelatedProductsService.instance;
    }
}

const useRelatedProducts = (productId: string) => {
    const [relatedProducts, setRelatedProducts] = useState<{
        kitsCollection: ProductModel[] | null,
        accessoryCollection: ProductModel[] | null,
        isDataFetched: Boolean
    }>({
        kitsCollection: null,
        accessoryCollection: null,
        isDataFetched: false,
    });

    useEffect(() => {
        const service = RelatedProductsService.getInstance();
        const fetchData = async () => {
            try {
                const fetchedData = await service.fetchRelatedProducts(productId);
                setRelatedProducts(fetchedData);
            } catch (error) {
                logger.error('Failed to fetch products:', error);
            }
        };
        fetchData();
    }, [productId]);

    return relatedProducts;
};

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    accordionStyle?: InjectableCss;
    accStrips?: InjectableCss;
    specificationTabs?: TabPresentationProps;
    hiddenForMobile: HiddenProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 0.375rem;
            svg {
                height: 20px;
                width: 20px;
            }
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        css: css`
            font-weight: 600 !important;
        `,
    },
    accordionStyle: {
        css: css`
            a {
                line-height: 2rem !important;
                &:hover {
                    text-decoration: underline !important;
                    text-size: 5rem !important;
                }
                span {
                    margin: 0;
                    margin-right: 1rem;
                    border: none;
                    font-size: 1rem;
                    line-height: 2rem !important;
                    font-family: "Lato", sans-serif;
                    span {
                        margin-right: 5px;
                        display: inline-block;
                        vertical-align: middle;
                    }
                    &::-moz-selection {
                        color: white;
                        background: #2060ad;
                    }

                    &::selection {
                        color: white;
                        background: #2060ad;
                    }
                }
            }
        `,
    },
    specificationTabs: {
        css: css``,
    },
    hiddenForMobile: {
        below: "md",
    },
    specificationsAccordionSection: {
        headerProps: {
            css: css`
                button {
                    border-top-color: transparent !important;
                    border-left-color: transparent !important;
                    border-right-color: transparent !important;
                    border-bottom: 2px solid #ececec !important;
                    margin-bottom: 1rem;
                    padding: 1rem 0.5rem 1rem 0;
                    height: 100%;
                    width: 100%;
                    background: #fff;
                    font-family: inherit;
                    display: unset;
                    text-align: left;
                    align-items: center;
                    justify-content: unset;
                    cursor: pointer;
                    position: relative;
                    outline: none !important;

                    &:after {
                        content: "";
                        display: block;
                        border-bottom: 2px solid #c63527 !important;
                        margin-bottom: -1.15rem !important;
                        width: 44px !important;
                        padding-top: 1rem;
                    }
                    &:focus {
                        border-top-color: transparent !important;
                        border-left-color: transparent !important;
                        border-right-color: transparent !important;
                        border-bottom: 2px solid #ececec !important;
                        outline: none !important;
                        padding: 1rem 0.5rem 1rem 0;
                        padding-left: 0 !important;
                        padding-top: 1rem !important;
                        border-width: 0 0 3px 0 !important;
                    }
                    span {
                        font-family: "Roboto", sans-serif;
                        font-size: 1.25rem;
                        font-weight: 600;
                        padding-left: 1rem;
                    }
                    span.toggle {
                        right: 1rem;
                        position: absolute;
                        top: 1.1rem;
                        padding-left: 0;
                        width: 1rem;
                    }
                    &:hover {
                        background: #f2f5f7 !important;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                border: none;
                line-height: 1.5rem;
                div span,
                a span,
                html body, html body ul li, html body p, p, ul li {
                    font-size: .925rem !important;
                    font-family:'Open Sans', sans-serif !important;
                    font-weight: 400;
                    color: #212529;
                    line-height: 24px;
                }
                font-size: .925rem !important;
                font-family:'Open Sans', sans-serif !important;
                color: #212529;
                line-height: 24px;
            }
            `,
        },
        titleTypographyProps: {
            css: css`
                color: #002554;
            `,
        },
    },
    accStrips: {
        css: css`
        font-size: .875rem !important;
        font-family: 'Lato', sans-serif !important;
        color: #505D68;
        line-height: 24px;

        .pdp-spacing {
            padding: 10px 0;

            > div {
                line-height: 2rem;
                position: relative;
                padding: 0 17px;

                &:nth-child(even) {
                    background: rgba(194, 209, 217, 0.21);
                }
                &:nth-child(odd) {
                    background: #fff;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            > div {               
                                &:nth-child(odd) {
                                    background: #fff;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        }
        `,
    }
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, id },
    fields: { showDocuments, showAttributes, showKitsProducts, showAccessoryProducts },
}) => {
    let featureFlags: string[] = ["TEUS-8092", "TEUS-8094", "TEUS-8086"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isFeatureOnDesktop = flagValues["TEUS-8092"];
    const isFeatureOnMobile = flagValues["TEUS-8094"];
    const openOptionsAndAccessoriesAccordian = flagValues["TEUS-8086"];

    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;

    const tabs: any = [];

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const { kitsCollection, accessoryCollection, isDataFetched } = useRelatedProducts(id);

    const [currentTab, setCurrentTab] = useState("");
    const loadData = (currentTab: string) => {
        const element = document.getElementById(`${currentTab}-trigger`);

        if (element?.getAttribute("aria-expanded") === "false") {
            element?.click();
        }
        element?.scrollIntoView({ behavior: "smooth" });
    };

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            loadData(tabKey);
            setCurrentTab(tabKey);
        }
    };

    const productAccordionTabs = (
        <TabGroup
            cssOverrides={{
                tabContent: css`
                    display: none;
                `,
                tabGroup: css`
                display:none;
                font-family: 'Roboto', sans-serif;
                font-size: 1.15rem;
                color:#002554;
                font-weight: 600;
                transition: all .4s;
                cursor: pointer;
                li  {
                    padding: 1rem 0;
                    margin: 0 2rem 0 0;
                    border-width: .250625rem;
                    &:hover {
                        border-bottom-color: #C63527;
                        border-width: .250625rem;
                    }
                    &:focus, selected {
                            border-bottom-color: #C63527 !important;
                    }
                    
                };
                li[aria-selected*="true"] {
                        border-bottom-color: #C63527 !important;
                    }
                
            }
                `,
                wrapper: css`
                    margin-bottom: 4rem;
                `,
            }}
            current={currentTab}
            onTabChange={onTabChange}
            mergeCss={true}
        >
            {tabs}
        </TabGroup>
    );

    const addTab = (tab: string, tabindex: string) => {
        tabs.push(<Tab {...styles.specificationTabs} headline={translate(tab)} key={tabindex} tabKey={tabindex}></Tab>);
    };
    const specificationTabs = (
        <>
            {renderingSpecifications &&
                specifications?.map((specification, index) => (
                    <ManagedAccordionSection
                        initialExpanded={true}
                        key={specification.id.toString()}
                        title={specification.nameDisplay}
                        data-test-selector={`productDetails_specifications_${index}`}
                        {...styles.specificationsAccordionSection}
                        uid={`productDetails_specifications_${index}`}
                        {...addTab(specification.nameDisplay, `productDetails_specifications_${index}`) as any}
                    >
                        {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                    </ManagedAccordionSection>
                ))}
        </>
    );
    const attributesAccordion = renderingAttributes && (
        <Typography as="div" {...styles.accStrips}>
            <ManagedAccordionSection
                title={translate("Attributes")}
                initialExpanded={true}
                data-test-selector="productDetails_specifications_attributes"
                {...styles.specificationsAccordionSection}
                uid="Attributes"
                {...addTab(translate("Attributes"), "Attributes") as any}
            >
                {brand && (
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{brand.name} </Typography>
                    </StyledWrapper>
                )}
                {attributeTypes &&
                    attributeTypes.map(attributeType => (
                        <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                            <Typography {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name}:&nbsp;
                            </Typography>
                            <Typography {...styles.attributeTypeValuesText}>
                                {attributeType
                                    .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </Typography>
                        </StyledWrapper>
                    ))}
            </ManagedAccordionSection>
        </Typography>
    );
    const documentsAccordion = renderingDocuments && (
        <Typography as="div" {...styles.accStrips}>
            <ManagedAccordionSection
                title={translate("Documents")}
                data-test-selector="productDetails_specifications_documents"
                initialExpanded={true}
                {...styles.specificationsAccordionSection}
                uid="Documents"
                {...addTab(translate("Documents"), "Documents") as any}
            >
                {documents!.map(document => (
                    <Typography key={document.id} as="div" {...styles.accordionStyle}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            data-test-selector={`productDetails_${document.id}`}
                            className="pdp-lnk-documents"
                        >
                            <Icon src={PdfIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </Typography>
                ))}

            </ManagedAccordionSection>
        </Typography>
    );

    const kitProductAccordion =
        showKitsProducts && isDataFetched && kitsCollection && kitsCollection?.length > 0 ? (
            <ManagedAccordionSection
                title={translate("Kit Contents")}
                initialExpanded={true}
                data-test-selector="productDetails_specifications_kits"
                {...styles.specificationsAccordionSection}
                uid="Kits"
                {...addTab(translate("Kit Contents"), "Kits") as any}
            >
                <ProductDetailsKitSection title={translate("Kit Contents")} configuration={kitsCollection} kitText={true}></ProductDetailsKitSection>
            </ManagedAccordionSection>
        ) : null;

    const accessoryProductAccordion =
        showAccessoryProducts && isDataFetched && accessoryCollection && accessoryCollection.length > 0 ? (
            <ManagedAccordionSection
                title={translate("Accessories")}
                initialExpanded={openOptionsAndAccessoriesAccordian}
                data-test-selector="productDetails_specifications_accessory"
                {...styles.specificationsAccordionSection}
                uid="Accessory"
                {...addTab(translate("Accessories"), "Accessories") as any}
            >
                <ProductDetailsKitSection title={translate("Accessories")} configuration={accessoryCollection} kitText={false}></ProductDetailsKitSection>
            </ManagedAccordionSection>
        ) : null;

    let sequenceComponents = [specificationTabs, attributesAccordion, documentsAccordion, kitProductAccordion, accessoryProductAccordion];
    if (isFeatureOnDesktop && !isMobile().any) {
        sequenceComponents = [documentsAccordion, specificationTabs, attributesAccordion, kitProductAccordion, accessoryProductAccordion];
    } else if (isFeatureOnMobile && isMobile().any) {
        sequenceComponents = [documentsAccordion, specificationTabs, attributesAccordion, kitProductAccordion, accessoryProductAccordion];
    }

    return (
        <>
            <Hidden {...styles.hiddenForMobile}>{productAccordionTabs}</Hidden>
            {sequenceComponents.map((Component, index) => (
                Component ? <React.Fragment key={index}>{Component}</React.Fragment> : null
            ))}
        </>
    );
};

export async function getRelatedProducts(
    parameter: GetRelatedProductCollectionApiV2Parameter,
): Promise<ProductCollectionModel> {
    const productCollection = await getRelatedProductsCollectionV2(parameter);
    return productCollection;
}

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },

            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showKitsProducts,
                displayName: "Show Kits Products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showAccessoryProducts,
                displayName: "Show Accessory Products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
        ],
    },
};

export default widgetModule;